import { LinkedAsset } from '@/types/AssetFolders';
import {
    DataModel,
    DataModelAttributeOption,
    DataModelCollectionFilteringFunction,
    DataModelCustomFilter,
    DataModelCustomLayoutFilter,
    DataModelGroupTitleFilter,
    DataModelItemTitleFilter,
    DataModelSortingFunction,
    DataModelUiReferenceFilter
} from '@/types/DataModel';

//eslint-disable-next-line
declare const angular: any;

//eslint-disable-next-line
export const getRootScope = (injector: any) => injector.get('$rootScope');

export const FILTERS = {
    MEDIA_ASSET_LAYOUT_TITLE: 'mediaAssetLayoutTitleFilterFilter'
};

export const getFilter = (filterName: string) => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();

    // AngularJS internally attaches 'Filter' to the filter name
    filterName = filterName + 'Filter';

    //eslint-disable-next-line
    return angularJsInjector.get(filterName);
};

export const getCustomFilter = (filterName: string) : DataModelCustomFilter | undefined => {
    return getFilter(filterName);
};

export const getCustomLayoutFilter = (filterName: string) : DataModelCustomLayoutFilter | undefined => {
    return getFilter(filterName);
};

export const getCustomCollectionFilter = <T>(filterName: string): DataModelCollectionFilteringFunction<T> | undefined => {
    return getFilter(filterName);
};

export const getCustomUiReferenceFilter = (filterName: string) : DataModelUiReferenceFilter | undefined => {
    return getFilter(filterName);
};

type GetSortingFunctionType = <Row, Value>(sortingFunctionName: string) => DataModelSortingFunction<Row, Value> | undefined;
export const getSortingFunction: GetSortingFunctionType  = (sortingFunctionName: string) => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();

    //eslint-disable-next-line
    return angularJsInjector.get(sortingFunctionName);
};

export const getItemTitleFilter = (filterName: string) : DataModelItemTitleFilter | undefined => {
    return getFilter(filterName);
};

export const getGroupTitleFilter = (filterName: string) : DataModelGroupTitleFilter | undefined => {
    return getFilter(filterName);
};

export const getSecondaryItemTitleFilter = (filterName: string) : DataModelItemTitleFilter | undefined => {
    return getFilter(filterName);
};

export const getDataModel = () => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();
    const rootScope = getRootScope(angularJsInjector);

    return rootScope.dataModel as DataModel;
};

/**
 * After React loads service-backed option list data, mirror it onto the shared AngularJS DataModel so
 * legacy paths (e.g. EnumAttributeService / optionListOptionsAsync) do not fire a duplicate GET .../options?serviceName=...
 *
 * Returns the mapped, normalized, and translated copy that was assigned to the
 * AngularJS DataModel. Callers should prefer this return value over their input
 * `options` array when populating React state, because `translateAllOptions`
 * runs in-place on the returned array (so `option.translatedOption` is set
 * here but not on the caller's original input).
 *
 * DRIFT-PARTNER — the field whitelist below
 * (`name, serviceName, key, value, label, labels, icon, icons, groups`)
 * must stay identical to the AngularJS server-fetch normalization at
 * `web/main/src/main/webapp/app/dataModel.js#fetchAndCacheOptionListAsync`
 * (~lines 1558-1570). Both produce the AngularJS-side option shape, just
 * from different inputs (raw server response there, already-normalized React
 * options here). Adding a new field on either side without the other will
 * cause AngularJS consumers (legacy editor + custom data-model filters) to
 * see different shapes depending on whether the data was loaded by AngularJS
 * or by React. See also the React-side normalizer
 * `components/datamodel/optionListUtils.ts#normalizeOptionListOption`.
 */
export const hydrateAngularOptionListFromOptions = (
    dataModel: DataModel,
    optionListName: string,
    options: DataModelAttributeOption[]
): DataModelAttributeOption[] => {
    if (!dataModel?.optionList) {
        return options;
    }
    const optionList = dataModel.optionList(optionListName);
    if (!optionList || typeof optionList !== 'object') {
        return options;
    }

    const mapped = options.map((option) => ({
        name: option.name ?? option.key,
        serviceName: option.serviceName || '',
        key: option.key ?? option.name,
        value: option.label ?? option.value,
        label: option.label ?? option.value,
        labels: option.labels,
        icon: option.icon,
        icons: option.icons,
        groups: option.groups,
    })) as DataModelAttributeOption[];

    optionList.options = mapped;
    optionList.optionsLoaded = true;
    delete optionList.optionsLoadingPromise;

    try {
        const injector = getInjector();
        const $rootScope = getRootScope(injector);
        if (typeof $rootScope.translateAllOptions === 'function') {
            $rootScope.translateAllOptions(null, optionList.options);
        }
    } catch {
        // No Angular injector (e.g. Storybook without shell)
    }

    return mapped;
};

export const getInjector = () => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();
    return angularJsInjector;
};

/**
 * Mirror `$rootScope.hasSettingFeature` from application-controller.js.
 * Returns false when the Angular shell is absent (Storybook, unit tests).
 */
export const hasSettingFeature = (feature: string): boolean => {
    try {
        const $rootScope = getRootScope(getInjector());
        if (typeof $rootScope.hasSettingFeature === 'function') {
            return $rootScope.hasSettingFeature(feature);
        }
    } catch {
        // No Angular injector
    }
    return false;
};

export const getCompiler = () => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();
    return angularJsInjector.get('$compile');
};

export const showAssetFoldersModal = (onFileSelected: (linkedAsset: LinkedAsset, linkedAssetPath: string) => void, acceptedTypes: string) => {
    const angularJsInjector = getInjector();
    //eslint-disable-next-line
    const $reactBridge = angularJsInjector.get('ReactBridge');
    $reactBridge.showAssetFoldersModal(onFileSelected, acceptedTypes);
};
